import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

import { performanceMonitor } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { handleIndexError, isIndexError } from '../utils/firebaseIndexes';
import { useRealtime } from './RealtimeContext';
import { userDataCache, testDataCache, courseDataCache } from '../utils/cacheManager';

const UserDataContext = createContext();

export function useUserData() {
  return useContext(UserDataContext);
}

// Using the advanced cache manager from cacheManager.js

export function UserDataProvider({ children }) {
  const { currentUser } = useRealtime();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // User Data States with better initialization
  const [userExams, setUserExams] = useState({});
  const [userMockTests, setUserMockTests] = useState([]);
  const [userPracticeSets, setUserPracticeSets] = useState([]);
  const [userNotes, setUserNotes] = useState([]);
  const [userCourses, setUserCourses] = useState([]);
  const [userCertificates, setUserCertificates] = useState([]);
  const [userAssignments, setUserAssignments] = useState([]);
  const [userDoubts, setUserDoubts] = useState([]);
  const [userUpcomingClasses, setUpcomingClasses] = useState([]);
  const [userPerformance, setUserPerformance] = useState({});
  const [userProgress, setUserProgress] = useState({});
  const [userPreferences, setUserPreferences] = useState({});
  const [userStats, setUserStats] = useState({});
  const [userRevisionData, setUserRevisionData] = useState({});

  // Track loading states for individual data types
  const [loadingStates, setLoadingStates] = useState({
    exams: false,
    mockTests: false,
    practiceSets: false,
    notes: false,
    courses: false,
    certificates: false,
    assignments: false,
    doubts: false,
    upcomingClasses: false,
    performance: false,
    progress: false,
    preferences: false,
    stats: false,
    revisionData: false
  });

  // Store unsubscribe functions for cleanup
  const [unsubscribers, setUnsubscribers] = useState(new Map());

  // Memoized user ID for dependency tracking
  const userId = useMemo(() => currentUser?.uid, [currentUser]);

  // Load all user data when user changes
  useEffect(() => {
    if (userId) {
      performanceMonitor.start('user_data_initialization');
      loadCriticalData();
    } else {
      clearAllUserData();
    }
  }, [userId]);

  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
      userDataCache.clear();
    };
  }, []);

  const setLoadingState = useCallback((key, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [key]: isLoading }));
  }, []);

  const loadCriticalData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Load essential data first (non-blocking)
      const criticalDataPromise = Promise.allSettled([
        loadUserStats(),
        loadUserPerformance(),
        loadUserPreferences()
      ]);
      
      // Set loading to false quickly to show dashboard
      setTimeout(() => {
        setLoading(false);
      }, 100);
      
      // Wait for critical data
      await criticalDataPromise;
      
      // Load remaining data in background
      loadRemainingData();
      
      performanceMonitor.end('user_data_initialization');
    } catch (error) {
      setError('Failed to load user data');
      setLoading(false);
      performanceMonitor.end('user_data_initialization');
    }
  };

  const loadRemainingData = async () => {
    try {
      // Load remaining data in parallel with individual loading states
      await Promise.allSettled([
        loadUserExams(),
        loadUserMockTests(),
        loadUserPracticeSets(),
        loadUserNotes(),
        loadUserCourses(),
        loadUserCertificates(),
        loadUserAssignments(),
        loadUserDoubts(),
        loadUserUpcomingClasses(),
        loadUserProgress(),
        loadUserRevisionData()
      ]);
    } catch (error) {
      }
  };

  const loadAllUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await Promise.allSettled([
        loadUserExams(),
        loadUserMockTests(),
        loadUserPracticeSets(),
        loadUserNotes(),
        loadUserCourses(),
        loadUserCertificates(),
        loadUserAssignments(),
        loadUserDoubts(),
        loadUserUpcomingClasses(),
        loadUserPerformance(),
        loadUserProgress(),
        loadUserPreferences(),
        loadUserStats(),
        loadUserRevisionData()
      ]);
      
    } catch (error) {
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const clearAllUserData = useCallback(() => {
    // Clear all state
    setUserExams({});
    setUserMockTests([]);
    setUserPracticeSets([]);
    setUserNotes([]);
    setUserCourses([]);
    setUserCertificates([]);
    setUserAssignments([]);
    setUserDoubts([]);
    setUpcomingClasses([]);
    setUserPerformance({});
    setUserProgress({});
    setUserPreferences({});
    setUserStats({});
    setUserRevisionData({});
    
    // Clear loading states
    setLoadingStates({
      exams: false,
      mockTests: false,
      practiceSets: false,
      notes: false,
      courses: false,
      certificates: false,
      assignments: false,
      doubts: false,
      upcomingClasses: false,
      performance: false,
      progress: false,
      preferences: false,
      stats: false,
      revisionData: false
    });
    
    // Clear error
    setError(null);
    
    // Unsubscribe from all listeners
    unsubscribers.forEach(unsubscribe => unsubscribe());
    setUnsubscribers(new Map());
    
    // Clear user-specific cache entries
    if (userId) {
      const userKeys = [
        `user_exams_${userId}`,
        `user_stats_${userId}`,
        `user_performance_${userId}`,
        `user_preferences_${userId}`,
        `user_progress_${userId}`,
        `user_revision_${userId}`
      ];
      userKeys.forEach(key => userDataCache.delete(key));
    }
  }, [unsubscribers, userId]);

  // Enhanced load functions with advanced caching and error handling
  const loadUserExams = async () => {
    if (!userId) return;
    
    const cacheKey = `user_exams_${userId}`;
    const cachedData = userDataCache.get(cacheKey, { prefetchRelated: true });
    if (cachedData) {
      setUserExams(cachedData);
      return;
    }
    
    setLoadingState('exams', true);
    
    try {
      const examDoc = await getDoc(doc(db, 'userExams', userId));
      if (examDoc.exists()) {
        const data = examDoc.data();
        setUserExams(data);
        userDataCache.set(cacheKey, data, { persistToFirebase: false });
      } else {
        // Create default exam preferences
        const defaultExams = {
          selectedExams: [],
          examPreferences: {},
          examHistory: [],
          examProgress: {}
        };
        await setDoc(doc(db, 'userExams', userId), defaultExams);
        setUserExams(defaultExams);
        userDataCache.set(cacheKey, defaultExams, { persistToFirebase: false });
      }
    } catch (error) {
      setError('Failed to load exam data');
    } finally {
      setLoadingState('exams', false);
    }
  };

  const loadUserMockTests = async () => {
    if (!userId) return;
    
    setLoadingState('mockTests', true);
    
    try {
      const mockTestsQuery = query(
        collection(db, 'testResults'),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc'),
        limit(20)
      );
      
      const unsubscribe = onSnapshot(mockTestsQuery, (snapshot) => {
        const mockTests = [];
        snapshot.forEach((doc) => {
          mockTests.push({ id: doc.id, ...doc.data() });
        });
        setUserMockTests(mockTests);
      }, (error) => {
        const indexError = handleIndexError(error, 'mock tests');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load mock test data');
      });
      
      // Store unsubscribe function
      setUnsubscribers(prev => new Map(prev).set('mockTests', unsubscribe));
      
    } catch (error) {
      setError('Failed to load mock test data');
    } finally {
      setLoadingState('mockTests', false);
    }
  };

  const loadUserPracticeSets = async () => {
    if (!userId) return;
    
    setLoadingState('practiceSets', true);
    
    try {
      const practiceQuery = query(
        collection(db, 'practiceSessions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      
      const unsubscribe = onSnapshot(practiceQuery, (snapshot) => {
        const practiceSets = [];
        snapshot.forEach((doc) => {
          practiceSets.push({ id: doc.id, ...doc.data() });
        });
        setUserPracticeSets(practiceSets);
      }, (error) => {
        const indexError = handleIndexError(error, 'practice sets');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load practice data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('practiceSets', unsubscribe));
      
    } catch (error) {
      setError('Failed to load practice data');
    } finally {
      setLoadingState('practiceSets', false);
    }
  };

  const loadUserNotes = async () => {
    if (!userId) return;
    
    setLoadingState('notes', true);
    
    try {
      const notesQuery = query(
        collection(db, 'notes'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      
      const unsubscribe = onSnapshot(notesQuery, (snapshot) => {
        const notes = [];
        snapshot.forEach((doc) => {
          notes.push({ id: doc.id, ...doc.data() });
        });
        setUserNotes(notes);
      }, (error) => {
        const indexError = handleIndexError(error, 'notes');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load notes data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('notes', unsubscribe));
      
    } catch (error) {
      setError('Failed to load notes data');
    } finally {
      setLoadingState('notes', false);
    }
  };

  const loadUserCourses = async () => {
    if (!userId) return;
    
    setLoadingState('courses', true);
    
    try {
      const coursesQuery = query(
        collection(db, 'userCourses'),
        where('userId', '==', userId),
        orderBy('enrolledAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(coursesQuery, (snapshot) => {
        const courses = [];
        snapshot.forEach((doc) => {
          courses.push({ id: doc.id, ...doc.data() });
        });
        setUserCourses(courses);
      }, (error) => {
        const indexError = handleIndexError(error, 'courses');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load course data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('courses', unsubscribe));
      
    } catch (error) {
      setError('Failed to load course data');
    } finally {
      setLoadingState('courses', false);
    }
  };

  const loadUserCertificates = async () => {
    if (!userId) return;
    
    setLoadingState('certificates', true);
    
    try {
      const certificatesQuery = query(
        collection(db, 'courseCertificates'),
        where('userId', '==', userId),
        orderBy('issuedAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(certificatesQuery, (snapshot) => {
        const certificates = [];
        snapshot.forEach((doc) => {
          certificates.push({ id: doc.id, ...doc.data() });
        });
        setUserCertificates(certificates);
      }, (error) => {
        const indexError = handleIndexError(error, 'certificates');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load certificate data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('certificates', unsubscribe));
      
    } catch (error) {
      setError('Failed to load certificate data');
    } finally {
      setLoadingState('certificates', false);
    }
  };

  const loadUserAssignments = async () => {
    if (!userId) return;
    
    setLoadingState('assignments', true);
    
    try {
      const assignmentsQuery = query(
        collection(db, 'courseAssignments'),
        where('userId', '==', userId),
        orderBy('dueDate', 'asc')
      );
      
      const unsubscribe = onSnapshot(assignmentsQuery, (snapshot) => {
        const assignments = [];
        snapshot.forEach((doc) => {
          assignments.push({ id: doc.id, ...doc.data() });
        });
        setUserAssignments(assignments);
      }, (error) => {
        const indexError = handleIndexError(error, 'assignments');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load assignment data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('assignments', unsubscribe));
      
    } catch (error) {
      setError('Failed to load assignment data');
    } finally {
      setLoadingState('assignments', false);
    }
  };

  const loadUserDoubts = async () => {
    if (!userId) return;
    
    setLoadingState('doubts', true);
    
    try {
      const doubtsQuery = query(
        collection(db, 'doubts'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(doubtsQuery, (snapshot) => {
        const doubts = [];
        snapshot.forEach((doc) => {
          doubts.push({ id: doc.id, ...doc.data() });
        });
        setUserDoubts(doubts);
      }, (error) => {
        const indexError = handleIndexError(error, 'doubts');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load doubts data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('doubts', unsubscribe));
      
    } catch (error) {
      setError('Failed to load doubts data');
    } finally {
      setLoadingState('doubts', false);
    }
  };

  const loadUserUpcomingClasses = async () => {
    if (!userId) return;
    
    setLoadingState('upcomingClasses', true);
    
    try {
      const classesQuery = query(
        collection(db, 'courseLiveClasses'),
        where('enrolledUsers', 'array-contains', userId),
        where('startTime', '>', new Date()),
        orderBy('startTime', 'asc')
      );
      
      const unsubscribe = onSnapshot(classesQuery, (snapshot) => {
        const classes = [];
        snapshot.forEach((doc) => {
          classes.push({ id: doc.id, ...doc.data() });
        });
        setUpcomingClasses(classes);
      }, (error) => {
        const indexError = handleIndexError(error, 'upcoming classes');
        if (indexError.type === 'index_required') {
          } else {
          }
        setError('Failed to load upcoming classes data');
      });
      
      setUnsubscribers(prev => new Map(prev).set('upcomingClasses', unsubscribe));
      
    } catch (error) {
      setError('Failed to load upcoming classes data');
    } finally {
      setLoadingState('upcomingClasses', false);
    }
  };

  const loadUserPerformance = async () => {
    if (!userId) return;
    
    const cacheKey = `performance_${userId}`;
    const cachedData = userDataCache.get(cacheKey);
    if (cachedData) {
      setUserPerformance(cachedData);
      return;
    }
    
    setLoadingState('performance', true);
    
    try {
      const performanceDoc = await getDoc(doc(db, 'userPerformance', userId));
      if (performanceDoc.exists()) {
        const data = performanceDoc.data();
        setUserPerformance(data);
        userDataCache.set(cacheKey, data);
      } else {
        // Create default performance data
        const defaultPerformance = {
          overallScore: 0,
          subjectScores: {},
          topicPerformance: {},
          improvementTrend: [],
          weakAreas: [],
          strongAreas: []
        };
        setUserPerformance(defaultPerformance);
        userDataCache.set(cacheKey, defaultPerformance);
        setDoc(doc(db, 'userPerformance', userId), defaultPerformance).catch(() => {});
      }
    } catch (error) {
      setError('Failed to load performance data');
      // Set fallback performance
      setUserPerformance({
        overallScore: 0,
        subjectScores: {},
        topicPerformance: {},
        improvementTrend: [],
        weakAreas: [],
        strongAreas: []
      });
    } finally {
      setLoadingState('performance', false);
    }
  };

  const loadUserProgress = async () => {
    if (!userId) return;
    
    const cacheKey = `progress_${userId}`;
    const cachedData = userDataCache.get(cacheKey);
    if (cachedData) {
      setUserProgress(cachedData);
      return;
    }
    
    setLoadingState('progress', true);
    
    try {
      const progressDoc = await getDoc(doc(db, 'userProgress', userId));
      if (progressDoc.exists()) {
        const data = progressDoc.data();
        setUserProgress(data);
        userDataCache.set(cacheKey, data);
      } else {
        // Create default progress data
        const defaultProgress = {
          courseProgress: {},
          examProgress: {},
          practiceProgress: {},
          overallProgress: 0
        };
        await setDoc(doc(db, 'userProgress', userId), defaultProgress);
        setUserProgress(defaultProgress);
        userDataCache.set(cacheKey, defaultProgress);
      }
    } catch (error) {
      setError('Failed to load progress data');
    } finally {
      setLoadingState('progress', false);
    }
  };

  const loadUserPreferences = async () => {
    if (!userId) return;
    
    const cacheKey = `preferences_${userId}`;
    const cachedData = userDataCache.get(cacheKey);
    if (cachedData) {
      setUserPreferences(cachedData);
      return;
    }
    
    setLoadingState('preferences', true);
    
    try {
      const preferencesDoc = await getDoc(doc(db, 'userPreferences', userId));
      if (preferencesDoc.exists()) {
        const data = preferencesDoc.data();
        setUserPreferences(data);
        userDataCache.set(cacheKey, data);
      } else {
        // Create default preferences
        const defaultPreferences = {
          examType: '',
          selectedExams: [],
          activeExam: null,
          subjects: [],
          notifications: true,
          emailNotifications: true,
          pushNotifications: true,
          theme: 'light',
          language: 'en',
          timezone: 'UTC'
        };
        await setDoc(doc(db, 'userPreferences', userId), defaultPreferences);
        setUserPreferences(defaultPreferences);
        userDataCache.set(cacheKey, defaultPreferences);
      }
    } catch (error) {
      setError('Failed to load preferences data');
    } finally {
      setLoadingState('preferences', false);
    }
  };

  const loadUserStats = async () => {
    if (!userId) return;
    
    const cacheKey = `stats_${userId}`;
    const cachedData = userDataCache.get(cacheKey);
    if (cachedData) {
      setUserStats(cachedData);
      return;
    }
    
    setLoadingState('stats', true);
    
    try {
      const statsDoc = await getDoc(doc(db, 'userStats', userId));
      if (statsDoc.exists()) {
        const data = statsDoc.data();
        setUserStats(data);
        userDataCache.set(cacheKey, data);
      } else {
        // Create default stats
        const defaultStats = {
          testsTaken: 0,
          totalTestsTaken: 0,
          totalPracticeSessions: 0,
          averageScore: 0,
          totalTime: 0,
          questionsAnswered: 0,
          correctAnswers: 0,
          coursesEnrolled: 0,
          certificatesEarned: 0,
          notesCreated: 0,
          totalNotes: 0,
          doubtsPosted: 0,
          totalDoubts: 0,
          studyStreak: 0
        };
        setUserStats(defaultStats);
        userDataCache.set(cacheKey, defaultStats);
        setDoc(doc(db, 'userStats', userId), defaultStats).catch(() => {});
      }
    } catch (error) {
      setError('Failed to load stats data');
      // Set fallback stats
      setUserStats({
        testsTaken: 0,
        totalTestsTaken: 0,
        totalPracticeSessions: 0,
        averageScore: 0,
        totalTime: 0,
        questionsAnswered: 0,
        correctAnswers: 0,
        coursesEnrolled: 0,
        certificatesEarned: 0,
        notesCreated: 0,
        totalNotes: 0,
        doubtsPosted: 0,
        totalDoubts: 0,
        studyStreak: 0
      });
    } finally {
      setLoadingState('stats', false);
    }
  };

  const loadUserRevisionData = async () => {
    if (!userId) return;
    
    const cacheKey = `revisionData_${userId}`;
    const cachedData = userDataCache.get(cacheKey);
    if (cachedData) {
      setUserRevisionData(cachedData);
      return;
    }
    
    setLoadingState('revisionData', true);
    
    try {
      const revisionDoc = await getDoc(doc(db, 'revisionData', userId));
      if (revisionDoc.exists()) {
        const data = revisionDoc.data();
        setUserRevisionData(data);
        userDataCache.set(cacheKey, data);
      } else {
        // Create default revision data
        const defaultRevisionData = {
          wrongQuestions: [],
          skippedQuestions: [],
          weakTopics: [],
          revisionHistory: [],
          lastRevisionDate: null
        };
        await setDoc(doc(db, 'revisionData', userId), defaultRevisionData);
        setUserRevisionData(defaultRevisionData);
        userDataCache.set(cacheKey, defaultRevisionData);
      }
    } catch (error) {
      setError('Failed to load revision data');
    } finally {
      setLoadingState('revisionData', false);
    }
  };

  // Enhanced update functions with advanced caching and batch operations
  const updateUserExams = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      performanceMonitor.start('update_user_exams');
      
      // Update local state immediately
      setUserExams(prev => ({ ...prev, ...updates }));
      
      // Update cache with new data
      const cacheKey = `user_exams_${userId}`;
      const cachedData = userDataCache.get(cacheKey, { fetchFromFirebase: false });
      if (cachedData) {
        userDataCache.set(cacheKey, { ...cachedData, ...updates });
      }
      
      // Queue Firebase update (will be batched)
      userDataCache.set(`user_exams_${userId}`, updates, { persistToFirebase: true });
      
      performanceMonitor.end('update_user_exams');
    } catch (error) {
      setError('Failed to update exam data');
      performanceMonitor.end('update_user_exams');
    }
  }, [userId]);

  const updateUserPerformance = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      performanceMonitor.start('update_user_performance');
      await updateDoc(doc(db, 'userPerformance', userId), updates);
      setUserPerformance(prev => ({ ...prev, ...updates }));
      
      // Update cache
      const cacheKey = `performance_${userId}`;
      const cachedData = userDataCache.get(cacheKey);
      if (cachedData) {
        userDataCache.set(cacheKey, { ...cachedData, ...updates });
      }
      
      performanceMonitor.end('update_user_performance');
    } catch (error) {
      setError('Failed to update performance data');
      performanceMonitor.end('update_user_performance');
    }
  }, [userId]);

  const updateUserProgress = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      performanceMonitor.start('update_user_progress');
      await updateDoc(doc(db, 'userProgress', userId), updates);
      setUserProgress(prev => ({ ...prev, ...updates }));
      
      // Update cache
      const cacheKey = `progress_${userId}`;
      const cachedData = userDataCache.get(cacheKey);
      if (cachedData) {
        userDataCache.set(cacheKey, { ...cachedData, ...updates });
      }
      
      performanceMonitor.end('update_user_progress');
    } catch (error) {
      setError('Failed to update progress data');
      performanceMonitor.end('update_user_progress');
    }
  }, [userId]);

  const updateUserPreferences = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      performanceMonitor.start('update_user_preferences');
      const prefsRef = doc(db, 'userPreferences', userId);
      
      try {
        // Try to update first (fast path when doc exists)
        await updateDoc(prefsRef, updates);
      } catch (error) {
        // If doc does not exist, create it with merge semantics
        await setDoc(prefsRef, updates, { merge: true });
      }
      
      setUserPreferences(prev => ({ ...prev, ...updates }));
      
      // Update cache
      const cacheKey = `preferences_${userId}`;
      const cachedData = userDataCache.get(cacheKey);
      if (cachedData) {
        userDataCache.set(cacheKey, { ...cachedData, ...updates });
      }
      
      performanceMonitor.end('update_user_preferences');
    } catch (error) {
      setError('Failed to update preferences data');
      performanceMonitor.end('update_user_preferences');
    }
  }, [userId]);

  const updateUserStats = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      performanceMonitor.start('update_user_stats');
      await updateDoc(doc(db, 'userStats', userId), updates);
      setUserStats(prev => ({ ...prev, ...updates }));
      
      // Update cache
      const cacheKey = `stats_${userId}`;
      const cachedData = userDataCache.get(cacheKey);
      if (cachedData) {
        userDataCache.set(cacheKey, { ...cachedData, ...updates });
      }
      
      performanceMonitor.end('update_user_stats');
    } catch (error) {
      setError('Failed to update stats data');
      performanceMonitor.end('update_user_stats');
    }
  }, [userId]);

  // Batch update function for multiple updates
  const batchUpdateUserData = useCallback(async (updates) => {
    if (!userId) return;
    
    try {
      performanceMonitor.start('batch_update_user_data');
      const batch = writeBatch(db);
      
      Object.entries(updates).forEach(([collection, data]) => {
        const docRef = doc(db, collection, userId);
        batch.update(docRef, data);
      });
      
      await batch.commit();
      
      // Update local state
      if (updates.userExams) {
        setUserExams(prev => ({ ...prev, ...updates.userExams }));
        const cacheKey = `exams_${userId}`;
        const cachedData = userDataCache.get(cacheKey);
        if (cachedData) {
          userDataCache.set(cacheKey, { ...cachedData, ...updates.userExams });
        }
      }
      
      if (updates.userPerformance) {
        setUserPerformance(prev => ({ ...prev, ...updates.userPerformance }));
        const cacheKey = `performance_${userId}`;
        const cachedData = userDataCache.get(cacheKey);
        if (cachedData) {
          userDataCache.set(cacheKey, { ...cachedData, ...updates.userPerformance });
        }
      }
      
      // Add more state updates as needed
      
      performanceMonitor.end('batch_update_user_data');
    } catch (error) {
      setError('Failed to update user data');
      performanceMonitor.end('batch_update_user_data');
    }
  }, [userId]);

  // Clear cache function
  const clearCache = useCallback(() => {
    userDataCache.clear();
    testDataCache.clear();
    courseDataCache.clear();
  }, []);

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    return {
      user: userDataCache.getStats(),
      test: testDataCache.getStats(),
      course: courseDataCache.getStats()
    };
  }, []);

  const value = useMemo(() => ({
    // Data
    userExams,
    userMockTests,
    userPracticeSets,
    userNotes,
    userCourses,
    userCertificates,
    userAssignments,
    userDoubts,
    userUpcomingClasses,
    userPerformance,
    userProgress,
    userPreferences,
    userStats,
    userRevisionData,
    
    // Loading states
    loading,
    loadingStates,
    error,
    
    // Update functions
    updateUserExams,
    updateUserPerformance,
    updateUserProgress,
    updateUserPreferences,
    updateUserStats,
    batchUpdateUserData,
    
    // Cache functions
    clearCache,
    getCacheStats,
    
    // Reload functions
    loadAllUserData,
    clearAllUserData
  }), [
    userExams,
    userMockTests,
    userPracticeSets,
    userNotes,
    userCourses,
    userCertificates,
    userAssignments,
    userDoubts,
    userUpcomingClasses,
    userPerformance,
    userProgress,
    userPreferences,
    userStats,
    userRevisionData,
    loading,
    loadingStates,
    error,
    updateUserExams,
    updateUserPerformance,
    updateUserProgress,
    updateUserPreferences,
    updateUserStats,
    batchUpdateUserData,
    clearCache,
    getCacheStats,
    loadAllUserData,
    clearAllUserData
  ]);

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}
